function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function money(value) {
  return Number(value || 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function paymentLabel(method) {
  const labels = {
    cash: 'Cash',
    gcash: 'GCash',
    bank: 'Bank transfer',
    credit_card: 'Credit card',
    card: 'Credit card',
    cod: 'COD / pending',
  };
  return labels[method] || method || '-';
}

function documentTitle(type) {
  const titles = {
    invoice: 'INVOICE',
    sales: 'INVOICE',
    proforma: 'PROFORMA / QUOTATION',
    purchase: 'PURCHASE',
    stock_return: 'STOCK RETURN',
    refund: 'REFUND',
    inventory_count: 'INVENTORY COUNT',
    loss_damage: 'LOSS & DAMAGE',
  };
  return titles[type] || 'DOCUMENT';
}

function documentNumberLabel(type) {
  if (type === 'proforma') return 'Quotation No.';
  if (type === 'sales' || type === 'invoice') return 'Invoice No.';
  return 'Document No.';
}

function companyAddress(company) {
  if (company.address) return company.address;
  return [
    company.buildingNumber,
    company.streetName,
    company.additionalStreetName,
    company.district,
    company.city,
    company.stateProvince,
    company.postalCode,
    company.country,
  ].filter(Boolean).join(', ');
}

function itemComments(item) {
  if (Array.isArray(item?.commentEntries)) {
    return item.commentEntries
      .map((entry) => ({ label: entry?.fromProduct ? 'SN' : 'Comment', text: String(entry?.text || '').trim() }))
      .filter((entry) => entry.text);
  }
  return (Array.isArray(item?.comments) ? item.comments : [])
    .map((text) => ({ label: /^sn\s*:/i.test(String(text)) ? '' : 'SN / Comment', text: String(text || '').trim() }))
    .filter((entry) => entry.text);
}

function itemLineGross(item) {
  const quantity = Number(item.qty ?? item.quantity) || 0;
  const price = Number(item.price ?? item.unitCost) || 0;
  return quantity * price;
}

function itemLineDiscount(item) {
  const gross = itemLineGross(item);
  const percent = Math.min(100, Math.max(0, Number(item.discountPercent) || 0));
  const amount = Math.max(0, Number(item.discountAmount) || 0);
  return Math.min(gross, (gross * percent / 100) + amount);
}

function itemLineDiscountLabel(item) {
  const parts = [];
  const percent = Number(item.discountPercent) || 0;
  const amount = Number(item.discountAmount) || 0;
  if (percent > 0) parts.push(`${money(percent).replace(/\.00$/, '')}%`);
  if (amount > 0) parts.push(`P${money(amount)}`);
  return parts.length ? parts.join(' + ') : '0.00%';
}

function safeDateLabel(value) {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-PH');
}

export function buildA4SalesDocumentHtml({ company = {}, sale = {}, documentType = 'invoice' }) {
  const type = documentType || sale.type || 'invoice';
  const title = documentTitle(type);
  const number = sale.documentNumber || sale.reference || sale.orderId || sale._id || '-';
  const dateLabel = safeDateLabel(sale.date);
  const dueDateLabel = safeDateLabel(sale.dueDate || sale.date);
  const stockDateLabel = safeDateLabel(sale.stockDate || sale.date);
  const items = Array.isArray(sale.items) ? sale.items : [];
  const itemGrossSubtotal = items.reduce((sum, item) => sum + itemLineGross(item), 0);
  const itemDiscountTotal = items.reduce((sum, item) => sum + itemLineDiscount(item), 0);
  const subtotal = items.length ? itemGrossSubtotal : Number(sale.subtotalAmount || 0);
  const discount = Number(sale.discountAmount) || itemDiscountTotal;
  const tax = Number(sale.taxAmount) || 0;
  const total = Number(sale.totalAmount ?? sale.total ?? (subtotal - discount + tax)) || 0;
  const paid = Number(sale.paidAmount) || 0;
  const change = Number(sale.changeAmount) || 0;
  const due = Math.max(0, total - Math.min(total, paid));
  const logo = company.logo || (typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '');
  const status = sale.paymentStatus || sale.status || (type === 'proforma' ? 'draft' : '-');
  const customerName = sale.customerName || sale.supplierId?.name || sale.supplierName || 'Unknown';
  const showItemPrices = sale.printOptions?.showItemPrices !== false;

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(title)} ${escapeHtml(number)}</title>
<style>
@page { size: A4 portrait; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; color: #000; font-family: Arial, sans-serif; font-size: 10pt; }
.sheet { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 15mm 16mm 12mm; background: #fff; display: flex; flex-direction: column; }
.top { display: flex; justify-content: space-between; gap: 10mm; padding-bottom: 4mm; border-bottom: 1px solid #8d8d8d; }
.title { font-size: 13pt; font-weight: 700; margin-bottom: 3mm; text-transform: uppercase; }
.company-name { font-weight: 700; margin-bottom: 1mm; }
.info-line { line-height: 1.45; }
.label { display: inline-block; min-width: 24mm; }
.logo-box { width: 47mm; height: 34mm; overflow: hidden; flex: 0 0 47mm; background: #fff; margin-top: 1mm; }
.logo { width: 50mm; height: 34mm; object-fit: fill; transform: translateX(-3mm); display: block; }
.meta { display: grid; grid-template-columns: 1fr 1fr; gap: 16mm; margin: 4mm 0 6mm; }
.meta-table { width: 100%; border-collapse: collapse; }
.meta-table td { padding: 0.7mm 0; vertical-align: top; }
.meta-table td:first-child { width: 32mm; }
.items { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
.items th, .items td { border: 1px solid #b8b8b8; padding: 1.5mm 2mm; vertical-align: top; }
.items th { background: #f3f3f3; text-align: center; font-weight: 700; }
.items .center { text-align: center; }
.items .num { text-align: right; white-space: nowrap; }
.subline { color: #333; font-size: 8.5pt; margin-top: 0.8mm; line-height: 1.25; }
.summary { width: 61mm; margin: 3mm 0 0 auto; border-collapse: collapse; font-size: 9.5pt; }
.summary td { border: 1px solid #b8b8b8; padding: 1.5mm 2mm; }
.summary td:last-child { text-align: right; font-weight: 700; }
.payment { width: 61mm; margin: 4mm 0 0 auto; line-height: 1.55; font-size: 9.5pt; }
.payment-row { display: flex; justify-content: space-between; gap: 8mm; }
.notes { margin-top: 6mm; min-height: 16mm; border: 1px solid #cfcfcf; padding: 2mm; color: #333; }
.footer { margin-top: auto; padding-top: 10mm; display: flex; justify-content: space-between; color: #666; font-size: 8pt; }
@media screen { body { background: #242424; padding: 12px; } .sheet { box-shadow: 0 0 0 1px #d8b600, 0 2px 12px rgba(0,0,0,.35); } }
@media print { body { background: #fff; } .sheet { margin: 0; box-shadow: none; } }
</style></head><body><main class="sheet">
  <header class="top">
    <div>
      <div class="title">${escapeHtml(title)}</div>
      <div class="company-name">${escapeHtml(company.name || 'iConnect Home Wireless Network & Data Solution')}</div>
      <div class="info-line">${escapeHtml(companyAddress(company) || '-')}</div>
      <div class="info-line"><span class="label">Tax No.:</span>${escapeHtml(company.taxId || '-')}</div>
      <div class="info-line"><span class="label">Phone:</span>${escapeHtml(company.phone || '-')}</div>
      <div class="info-line"><span class="label">Email:</span>${escapeHtml(company.email || '-')}</div>
    </div>
    ${logo ? `<div class="logo-box"><img class="logo" src="${escapeHtml(logo)}" alt="Company logo"></div>` : ''}
  </header>
  <section class="meta">
    <div>
      <div><strong>Bill to</strong></div>
      <div>${escapeHtml(customerName || 'Unknown')}</div>
      <div>${escapeHtml(sale.customerAddress || '-')}</div>
      <div>${escapeHtml(sale.customerPhone || '-')}</div>
      ${sale.customerEmail ? `<div>${escapeHtml(sale.customerEmail)}</div>` : ''}
    </div>
    <table class="meta-table"><tbody>
      <tr><td>${escapeHtml(documentNumberLabel(type))}</td><td>${escapeHtml(number)}</td></tr>
      <tr><td>Date:</td><td>${escapeHtml(dateLabel)}</td></tr>
      <tr><td>Due date:</td><td>${escapeHtml(dueDateLabel)}</td></tr>
      <tr><td>Payment status:</td><td>${escapeHtml(status)}</td></tr>
      <tr><td>Payment method:</td><td>${escapeHtml(paymentLabel(sale.paymentMethod))}</td></tr>
      ${sale.paymentReference ? `<tr><td>Reference:</td><td>${escapeHtml(sale.paymentReference)}</td></tr>` : ''}
      <tr><td>Cashier:</td><td>${escapeHtml(sale.createdBy || '-')}</td></tr>
      <tr><td>Stock date:</td><td>${escapeHtml(stockDateLabel)}</td></tr>
    </tbody></table>
  </section>
  <table class="items"><thead><tr><th style="width:10mm">#</th><th>Item</th><th style="width:18mm">Quantity</th>${showItemPrices ? '<th style="width:25mm">Unit price</th><th style="width:18mm">Tax</th><th style="width:20mm">Discount</th><th style="width:27mm">Total</th>' : ''}</tr></thead><tbody>
    ${items.map((item, index) => {
      const quantity = Number(item.qty ?? item.quantity) || 0;
      const price = Number(item.price ?? item.unitCost) || 0;
      const comments = itemComments(item);
      const lineTotal = Math.max(0, itemLineGross(item) - itemLineDiscount(item));
      return `<tr><td class="center">${index + 1}</td><td>${escapeHtml(item.name || item.productName || '-')}${item.sku ? `<div class="subline">Code: ${escapeHtml(item.sku)}</div>` : ''}${comments.map((comment) => `<div class="subline">${comment.label ? `${escapeHtml(comment.label)}: ` : ''}${escapeHtml(comment.text)}</div>`).join('')}</td><td class="num">${money(quantity).replace(/\.00$/, '')}</td>${showItemPrices ? `<td class="num">${money(price)}</td><td class="center">${tax ? money(tax) : '---'}</td><td class="num">${escapeHtml(itemLineDiscountLabel(item))}</td><td class="num"><strong>${money(lineTotal)}</strong></td>` : ''}</tr>`;
    }).join('') || `<tr><td colspan="${showItemPrices ? 7 : 3}" class="center">No items</td></tr>`}
  </tbody></table>
  <table class="summary"><tbody>
    ${showItemPrices ? `<tr><td>Subtotal</td><td>P${money(subtotal)}</td></tr>
    <tr><td>Discount</td><td>P${money(discount)}</td></tr>
    <tr><td>Tax</td><td>P${money(tax)}</td></tr>` : ''}
    <tr><td><strong>Total</strong></td><td>P${money(total)}</td></tr>
  </tbody></table>
  <section class="payment">
    <div class="payment-row"><strong>Payment method:</strong><span>${escapeHtml(paymentLabel(sale.paymentMethod))}</span></div>
    ${sale.paymentReference ? `<div class="payment-row"><strong>Reference:</strong><span>${escapeHtml(sale.paymentReference)}</span></div>` : ''}
    <div class="payment-row"><strong>Paid amount:</strong><span>P${money(paid)}</span></div>
    ${change > 0 ? `<div class="payment-row"><strong>Change:</strong><span>P${money(change)}</span></div>` : ''}
    <div class="payment-row"><strong>Amount due:</strong><span>P${money(due)}</span></div>
  </section>
  ${sale.reason || sale.internalNote || sale.note ? `<section class="notes">${escapeHtml(sale.reason || sale.internalNote || sale.note)}</section>` : ''}
  <footer class="footer"><span>Created with iConnectStore</span><span>Page 1</span></footer>
</main></body></html>`;
}

export function openPrintableDocument(html, { autoPrint = true } = {}) {
  const popup = window.open('', '_blank');
  if (!popup) return false;
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  if (autoPrint) {
    window.setTimeout(() => popup.print(), 350);
  }
  return true;
}
